import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { X, CheckCircle2, Clock, Circle, Send, Monitor, Loader2, Trash2, ExternalLink, Eye, MoreVertical, Paperclip } from 'lucide-react';
import type { Site } from '../types';
import {
  getFeedbackReplies,
  createAdminReply,
  getScreenshotUrl,
  type FeedbackTicket,
  type FeedbackReply,
} from '@/lib/feedbackService';
import { toast } from '@/hooks/use-toast';
import { callSiteApi } from '../utils';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';



interface FeedbackDetailPanelProps {
  ticket: FeedbackTicket;
  site: Site;
  onClose: () => void;
  onStatusChange: (id: string, status: FeedbackTicket['status'], priority?: FeedbackTicket['priority']) => void;
  onDelete: (id: string) => void;
}

const STATUS_OPTIONS: { value: FeedbackTicket['status']; label: string; icon: any; cls: string }[] = [
  { value: 'open',        label: 'Open',        icon: Circle,       cls: 'text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20' },
  { value: 'in_progress', label: 'In Progress', icon: Clock,        cls: 'text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20' },
  { value: 'review',      label: 'Review',      icon: Eye,          cls: 'text-violet-600 dark:text-violet-400 border-violet-200 dark:border-violet-700 bg-violet-50 dark:bg-violet-900/20' },
  { value: 'resolved',    label: 'Resolved',    icon: CheckCircle2, cls: 'text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20' },
];

const PRIORITY_OPTIONS: { value: NonNullable<FeedbackTicket['priority']>; label: string; cls: string }[] = [
  { value: 'low',      label: 'Low',      cls: 'text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/20' },
  { value: 'medium',   label: 'Medium',   cls: 'text-yellow-600 dark:text-yellow-400 border-yellow-200 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20' },
  { value: 'high',     label: 'High',     cls: 'text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20' },
  { value: 'critical', label: 'Critical', cls: 'text-red-600 dark:text-red-400 border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20' },
];

export function FeedbackDetailPanel({
  ticket,
  site,
  onClose,
  onStatusChange,
  onDelete,
}: FeedbackDetailPanelProps) {
  const [replies, setReplies]       = useState<FeedbackReply[]>([]);
  const [loadingReplies, setLoadingReplies] = useState(true);
  const [replyText, setReplyText]   = useState('');
  const [sending, setSending]       = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [imgError, setImgError]     = useState(false);
  const [adminAvatar, setAdminAvatar] = useState<string | null>(null);
  const [replyAttachmentBase64, setReplyAttachmentBase64] = useState<string | null>(null);

  useEffect(() => {
    if (site.defaultWpUserId) {
      callSiteApi(site.url, site.publicKey, site.secretKey, 'core/users')
        .then(data => {
          if (data?.users) {
            const match = data.users.find((u: any) => u.id === site.defaultWpUserId);
            if (match && match.avatar_url) setAdminAvatar(match.avatar_url);
          }
        })
        .catch(console.error);
    }
  }, [site.id, site.defaultWpUserId]);

  useEffect(() => {
    setLoadingReplies(true);
    setImgError(false);
    getFeedbackReplies(ticket.id)
      .then(setReplies)
      .catch(err => toast({ title: 'Error loading replies', description: err.message, variant: 'destructive' }))
      .finally(() => setLoadingReplies(false));

    setScreenshotUrl(getScreenshotUrl(ticket.screenshot_path));
  }, [ticket.id, ticket.screenshot_path]);

  const getUserProfile = async () => {
    return site.defaultWpUserName || 'Admin';
  };

  const handleSendReply = async () => {
    if (!replyText.trim() && !replyAttachmentBase64) return;
    setSending(true);
    try {
      const authorName = await getUserProfile();
      const content = replyText.trim() || 'Sent an attachment';
      const reply = await createAdminReply(ticket.id, content, authorName, undefined, replyAttachmentBase64);
      setReplies(prev => [...prev, reply]);
      setReplyText('');
      setReplyAttachmentBase64(null);
    } catch (err: any) {
      toast({ title: 'Failed to send reply', description: err.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (ev) => {
        setReplyAttachmentBase64(ev.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const pagePath = ticket.page_url.replace(/^https?:\/\/[^/]+/, '') || '/';

  return createPortal(
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
      />

      {/* Slide-out panel */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 26, stiffness: 200 }}
        className="fixed right-0 top-0 h-full w-full max-w-lg bg-card border-l border-border shadow-2xl z-50 flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/20 flex-shrink-0">
          <div>
            <h3 className="text-base font-bold text-foreground">Feedback Details</h3>
            <a
              href={ticket.page_url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 mt-0.5"
            >
              {pagePath}
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  title="More actions"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-32">
                <DropdownMenuItem 
                  destructive 
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this ticket?')) {
                      onDelete(ticket.id);
                      onClose();
                    }
                  }}
                  className="cursor-pointer gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* Device info */}
          <div className="px-6 border-b border-border">
            <Accordion type="single" collapsible>
              <AccordionItem value="device-info" className="border-none">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-2">
                    <Monitor className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Device Info</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="bg-muted/30 border border-border/50 rounded-xl p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {ticket.viewport_width > 0 && (
                        <div>
                          <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">Viewport</p>
                          <p className="text-sm font-medium text-foreground">
                            {ticket.viewport_width} × {ticket.viewport_height}
                          </p>
                        </div>
                      )}
                      {ticket.created_at && (
                        <div>
                          <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">Submitted At</p>
                          <p className="text-sm font-medium text-foreground">
                            {new Date(ticket.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                          </p>
                        </div>
                      )}
                    </div>
                    {ticket.user_agent && (
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">User Agent</p>
                        <p className="text-xs text-muted-foreground/80 break-all leading-relaxed bg-background border border-border rounded-lg p-3 font-mono">
                          {ticket.user_agent}
                        </p>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Status & Priority controls */}
          <div className="px-6 py-4 border-b border-border grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Priority</p>
              <select
                value={ticket.priority || 'low'}
                onChange={e => onStatusChange(ticket.id, ticket.status, e.target.value as FeedbackTicket['priority'])}
                className={`w-full text-sm font-semibold border rounded-lg px-3 py-2 cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring transition-colors ${
                  PRIORITY_OPTIONS.find(opt => opt.value === (ticket.priority || 'low'))?.cls || 'bg-background text-foreground border-border'
                }`}
              >
                {PRIORITY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value} className="bg-background text-foreground font-medium">{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Status</p>
              <select
                value={ticket.status}
                onChange={e => onStatusChange(ticket.id, e.target.value as FeedbackTicket['status'], ticket.priority)}
                className={`w-full text-sm font-semibold border rounded-lg px-3 py-2 cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring transition-colors ${
                  STATUS_OPTIONS.find(opt => opt.value === ticket.status)?.cls || 'bg-background text-foreground border-border'
                }`}
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value} className="bg-background text-foreground font-medium">{opt.label}</option>
                ))}
              </select>
            </div>
          </div>


          {/* Comment thread */}
          <div className="px-6 py-4">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4">Comments</p>
            <div className="space-y-3">
              {/* Original comment */}
              <div className="bg-muted/30 border border-border/50 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                    {ticket.author_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-foreground">{ticket.author_name}</p>
                    {ticket.author_email && <p className="text-[10px] text-muted-foreground">{ticket.author_email}</p>}
                  </div>
                </div>
                <p className="text-sm text-foreground leading-relaxed">{ticket.comment}</p>
                {screenshotUrl && !imgError && (
                  <div 
                    className="mt-3 relative rounded-lg overflow-hidden border border-border bg-muted/20 shadow-sm group"
                  >
                    <a href={screenshotUrl} target="_blank" rel="noreferrer" className="block relative">
                      <img
                        src={screenshotUrl}
                        alt="Feedback screenshot"
                        className="w-full h-32 object-cover object-top transition-opacity group-hover:opacity-90"
                        onError={() => setImgError(true)}
                      />
                      {/* Pin overlay */}
                      <div
                        className="absolute w-5 h-5 -translate-x-1/2 -translate-y-full"
                        style={{ left: `${ticket.x_percent}%`, top: `${ticket.y_percent}%` }}
                      >
                        <div className="w-5 h-5 rounded-full rounded-bl-none bg-red-500 rotate-[-45deg] shadow-lg border-2 border-white" />
                      </div>
                    </a>
                  </div>
                )}
              </div>

              {/* Replies */}
              {loadingReplies ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                replies.map(reply => (
                  <div
                    key={reply.id}
                    className={`rounded-xl px-4 py-3 border ${
                      reply.author_role === 'admin'
                        ? 'bg-workspace-accent/5 border-workspace-accent/20 ml-4'
                        : 'bg-muted/30 border-border/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      {reply.author_role === 'admin' && adminAvatar ? (
                        <img 
                          src={adminAvatar} 
                          alt={reply.author_name} 
                          className="w-6 h-6 rounded-full flex-shrink-0 object-cover" 
                        />
                      ) : (
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 ${
                          reply.author_role === 'admin'
                            ? 'bg-gradient-to-br from-emerald-500 to-teal-600'
                            : 'bg-gradient-to-br from-slate-500 to-slate-600'
                        }`}>
                          {reply.author_name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="text-xs font-bold text-foreground flex items-center gap-1.5">
                          {reply.author_name}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {new Date(reply.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground leading-relaxed">{reply.content}</p>
                    {reply.screenshot_path && (
                      <div className="mt-3 relative rounded-lg overflow-hidden border border-border bg-muted/20">
                        <a href={getScreenshotUrl(reply.screenshot_path) || '#'} target="_blank" rel="noreferrer" className="block relative">
                          <img
                            src={getScreenshotUrl(reply.screenshot_path) || ''}
                            alt="Reply Attachment"
                            className="w-full h-32 object-cover object-top transition-opacity hover:opacity-90"
                          />
                        </a>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Reply composer (fixed at bottom) */}
        <div className="px-6 py-4 border-t border-border bg-card flex-shrink-0">
          {replyAttachmentBase64 && (
            <div className="relative mb-3 inline-block">
              <img src={replyAttachmentBase64} alt="Attachment preview" className="max-h-32 rounded-lg border border-border" />
              <button
                onClick={() => setReplyAttachmentBase64(null)}
                className="absolute -top-2 -right-2 bg-background border border-border rounded-full p-1 shadow-sm text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          <div className="flex gap-2 items-center">
            <label
              htmlFor="admin-reply-upload"
              className="p-2.5 bg-background border border-border rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer flex-shrink-0"
              title="Attach an image"
            >
              <Paperclip className="w-5 h-5" />
            </label>
            <input
              type="file"
              id="admin-reply-upload"
              accept="image/jpeg, image/png, image/webp, image/avif"
              className="hidden"
              onChange={handleFileChange}
            />
            <input
              type="text"
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendReply(); } }}
              placeholder="Reply as admin…"
              className="flex-1 bg-background border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
            />
            <button
              onClick={handleSendReply}
              disabled={sending || (!replyText.trim() && !replyAttachmentBase64)}
              className="flex items-center gap-2 px-4 py-2.5 bg-workspace-accent text-white rounded-xl text-sm font-semibold hover:bg-workspace-accent-hover transition-colors disabled:opacity-50"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send
            </button>
          </div>
        </div>
      </motion.div>
    </>,
    document.body
  );
}
